import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { StoreRatingSummary, StoreReview } from '../store/store.service';

/** an approved comment on a tool */
export interface ToolPublicReview {
  tool: string;
  rating: number;
  comment: string;
  displayName: string;
  date: string;
}

/** GET /tool-reviews */
export interface ToolReviews {
  ok: boolean;
  tools: Record<string, StoreRatingSummary>;
  reviews: ToolPublicReview[];
}

const CLIENT_KEY = 'codigrate.client.v1';

/**
 * Ratings of the web tools (owner, 2026-10-04): every tool page ends with them. No account:
 * the browser keeps a random id, one review per tool and browser, editable. Every review
 * shows once the owner approves it.
 */
@Injectable( {
  providedIn: 'root',
} )
export class ToolReviewService {

  private list$?: Observable<ToolReviews>;
  private memoryClientId = '';

  constructor( private http: HttpClient ) {
  }

  /** approved ratings per tool + comments, fetched once per visit */
  reviews(): Observable<ToolReviews> {
    if ( !this.list$ ) {
      this.list$ = this.http.get<ToolReviews>( `${ environment.serverUrl }/tool-reviews` ).pipe(
        catchError( () => of( { ok: false, tools: {}, reviews: [] } as ToolReviews ) ),
        shareReplay( 1 ),
      );
    }
    return this.list$;
  }

  /** this browser's review of a tool, null when none (or the API is not there) */
  mine( tool: string ): Observable<{ ok: boolean; review: StoreReview | null }> {
    return this.http.get<{ ok: boolean; review: StoreReview | null }>(
      `${ environment.serverUrl }/tool-reviews/${ encodeURIComponent( tool ) }/mine`, { params: { clientId: this.clientId() } },
    ).pipe( catchError( () => of( { ok: false, review: null } ) ) );
  }

  submit( tool: string, rating: number, comment: string, displayName: string ): Observable<{ ok: boolean; review: StoreReview }> {
    return this.http.post<{ ok: boolean; review: StoreReview }>(
      `${ environment.serverUrl }/tool-reviews/${ encodeURIComponent( tool ) }`,
      { rating, comment: comment || undefined, displayName: displayName || undefined, clientId: this.clientId() },
    );
  }

  /** the browser's random id: kept in localStorage, in memory where storage is off */
  private clientId(): string {
    try {
      let id = localStorage.getItem( CLIENT_KEY ) || '';
      if ( !/^[A-Za-z0-9_-]{16,64}$/.test( id ) ) {
        id = this.newId();
        localStorage.setItem( CLIENT_KEY, id );
      }
      return id;
    } catch {
      if ( !this.memoryClientId ) {
        this.memoryClientId = this.newId();
      }
      return this.memoryClientId;
    }
  }

  private newId(): string {
    const bytes = new Uint8Array( 18 );
    crypto.getRandomValues( bytes );
    return Array.from( bytes, b => b.toString( 16 ).padStart( 2, '0' ) ).join( '' );
  }
}
